import * as React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Alert, Button } from 'reactstrap';
import { State } from '../../redux/state';
import { asPage } from '../../utils/wrappers/asPage';
import { LProps, withLocalization, withRouterAndHelpers, RouterProps } from '../../utils/wrappers';
import { userApiBasePath } from '../../redux/selectors/userApi';
import * as selectors from '../../redux/selectors';
import Footer from '../../shared/Footer';
import { Routes, makeSimpleRoutePath } from '../../utils/routerHelper';
import { formatAdminDateTime, competitionTimeZone } from '../../utils/adminDateTime';
import './sendMessageDetail.scss';

interface BroadcastDetail {
  id: number;
  subject: string;
  body: string;
  audienceType: string;
  audienceEntityID?: number;
  channels: string;
  recipientCount: number;
  status: string;
  createdAt: string;
  sentAt?: string;
  senderUserID: number;
}

interface Props {
  apiBase: string;
  token: string;
  competitionName: string;
  entities: { [id: number]: { id: number; name: string; type: string } };
  competitionTimezone?: string;
}

interface LocalState {
  broadcast: BroadcastDetail | null;
  recipientEmails: string[];
  loading: boolean;
  error: string;
}

class SendMessageDetailPage extends React.Component<LProps<RouterProps<{}> & Props>, LocalState> {
  public state: LocalState = {
    broadcast: null,
    recipientEmails: [],
    loading: true,
    error: ''
  };

  public componentDidMount() {
    this.load();
  }

  private broadcastID = (): number => {
    const match = this.props.history.location.pathname.match(/\/send-message\/(\d+)/);
    return match ? Number(match[1]) : NaN;
  }

  private authHeaders = () => ({
    'Authorization': this.props.token,
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  })

  private formatWhen = (iso: string) =>
    formatAdminDateTime(iso, this.props.competitionTimezone)

  private load = async () => {
    const { apiBase, competitionName } = this.props;
    const broadcastID = this.broadcastID();
    if (Number.isNaN(broadcastID)) {
      this.setState({ loading: false, error: 'Invalid message ID.' });
      return;
    }
    try {
      const res = await fetch(
        `${apiBase}/competitions/${encodeURIComponent(competitionName)}/broadcasts/${broadcastID}`,
        { headers: this.authHeaders() }
      );
      if (res.status === 403) {
        this.setState({ loading: false, error: 'You do not have permission to view this message.' });
        return;
      }
      if (res.status === 404) {
        this.setState({ loading: false, error: 'Message not found.' });
        return;
      }
      if (!res.ok) {
        this.setState({ loading: false, error: 'Failed to load message.' });
        return;
      }
      const data = await res.json();
      this.setState({
        broadcast: data.broadcast || null,
        recipientEmails: data.recipientEmails || [],
        loading: false,
        error: ''
      });
    } catch (_err) {
      this.setState({ loading: false, error: 'Failed to load message.' });
    }
  }

  private audienceLabel = (broadcast: BroadcastDetail): string => {
    const { entities, t } = this.props;
    if (broadcast.audienceType === 'competition') {
      return t('Entire competition');
    }
    if (broadcast.audienceType === 'admins') {
      return t('All admins');
    }
    if (broadcast.audienceType === 'users') {
      return t('Specific users');
    }
    if (broadcast.audienceType === 'preregistered') {
      return t('Pre-registered, not joined');
    }
    if (broadcast.audienceEntityID && entities[broadcast.audienceEntityID]) {
      return entities[broadcast.audienceEntityID].name;
    }
    return broadcast.audienceType;
  }

  private duplicateToCompose = () => {
    const { broadcast } = this.state;
    const { history, t } = this.props;
    if (!broadcast) {
      return;
    }
    history.push(makeSimpleRoutePath(Routes.SendMessage, t), {
      duplicateSubject: broadcast.subject,
      duplicateBody: broadcast.body,
      duplicateChannels: broadcast.channels.includes('email') && broadcast.channels.includes('in_app')
        ? 'both'
        : broadcast.channels.includes('email') ? 'email' : 'in_app'
    });
  }

  public render() {
    const { t, history } = this.props;
    const { broadcast, recipientEmails, loading, error } = this.state;
    return (
      <div>
        <div className="container cc__send_message_detail" style={{ maxWidth: 900, paddingTop: 24, paddingBottom: 40 }}>
          <Button
            color="link"
            className="cc__send_message_detail__back"
            onClick={() => history.push(makeSimpleRoutePath(Routes.SendMessage, t))}
          >{t('Back to Send Message')}</Button>
          <h2>{t('Sent Message')}</h2>
          {error && <Alert color="danger">{error}</Alert>}
          {loading && <div className="cc__lighttext">{t('Loading…')}</div>}
          {!loading && broadcast &&
            <div>
              <dl className="cc__send_message_detail__meta">
                <dt>{t('When')}</dt>
                <dd>{this.formatWhen(broadcast.sentAt || broadcast.createdAt)}</dd>
                <dt>{t('Subject')}</dt>
                <dd>{broadcast.subject}</dd>
                <dt>{t('Audience')}</dt>
                <dd>{this.audienceLabel(broadcast)}</dd>
                <dt>{t('Channels')}</dt>
                <dd>{broadcast.channels}</dd>
                <dt>{t('Recipients')}</dt>
                <dd>{broadcast.recipientCount}</dd>
                <dt>{t('Status')}</dt>
                <dd>{broadcast.status}</dd>
              </dl>
              <div className="cc__send_message_detail__body">
                <h3>{t('Message')}</h3>
                <div className="cc__send_message_detail__body__content">{broadcast.body}</div>
              </div>
              <div className="cc__send_message_detail__recipients">
                <h3>{t('Recipient emails')} ({recipientEmails.length})</h3>
                <p className="cc__lighttext">
                  {t('Visible to authorized admins on this detail view only.')}
                </p>
                {recipientEmails.length === 0 &&
                  <div className="cc__lighttext">{t('No recipient emails recorded for this message.')}</div>
                }
                {recipientEmails.length > 0 &&
                  <ul>
                    {recipientEmails.map((email) =>
                      <li key={email}>{email}</li>
                    )}
                  </ul>
                }
              </div>
              <Button color="secondary" outline={true} onClick={this.duplicateToCompose}>
                {t('Duplicate message')}
              </Button>
            </div>
          }
        </div>
        <Footer />
      </div>
    );
  }
}

const mapStateToProps = (state: State): Props => ({
  apiBase: userApiBasePath(state),
  token: state.token || '',
  competitionName: (state.competition && state.competition.name) || selectors.competitionName(state) || '',
  entities: (state.entities || {}) as any,
  competitionTimezone: competitionTimeZone(state.competition)
});

export default asPage({ requiresCompetitionStart: false, authenticated: true })(compose(
  withLocalization('account'),
  withRouterAndHelpers,
  connect(mapStateToProps)
)(SendMessageDetailPage));
